import { Divider, Stack, Switch, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { EnforceSSOModalContent } from '@modules/workspaceSettings/components/SSOSettings/EnforceSSOModalContent';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useUpdateTenantSsoMutation } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { useFetchTenantSSOSettings } from '@modules/workspaceSettings/hooks';
import { FETCH_TENANT_SSO_SETTINGS } from '@modules/workspaceSettings/hooks/useFetchTenantSSOSettings';

export const SSOEnforcementSwitch = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { ssoSettings, isLoading } = useFetchTenantSSOSettings();
  const isEnforced = ssoSettings?.isEnforced;
  const isSSOEnabled = ssoSettings?.isEnabled;

  const { openModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [isSSOEnforced, setIsSSOEnforced] = useState(Boolean(isEnforced));

  useEffect(() => {
    setIsSSOEnforced(Boolean(isEnforced));
  }, [isEnforced]);

  const [updateTenantSSO] = useUpdateTenantSsoMutation({
    variables: {
      request: {
        tenantID,
        isEnforced: false,
      },
    },
    refetchQueries: [FETCH_TENANT_SSO_SETTINGS],
  });

  const openEnforceSSOModal = () => {
    openModal({
      contentComponent: () => (
        <EnforceSSOModalContent onClose={() => setIsSSOEnforced(false)} />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
        shouldDisableBackdropClick: true,
      },
    });
  };

  const removeSSOEnforcement = async () => {
    try {
      await updateTenantSSO();
    } catch (error) {
      setIsSSOEnforced(isSSOEnforced);
      showErrorNotification({
        contentComponent: 'Failed to revert SSO Enforcement for organization',
        props: { isClosable: true },
      });
    }
  };

  const onEnforceSSOToggle = async () => {
    const updatedIsSSOEnforced = !isSSOEnforced;
    setIsSSOEnforced(updatedIsSSOEnforced);

    if (updatedIsSSOEnforced !== Boolean(isEnforced)) {
      if (updatedIsSSOEnforced) {
        openEnforceSSOModal();
      } else {
        await removeSSOEnforcement();
      }
    }
  };

  if (!isSSOEnabled) {
    return null;
  }

  return (
    <>
      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1">Enforce SSO for Organization</Typography>
          <Tooltip
            arrow
            placement="top"
            title="Enforcing SSO would require all users of your organization to authenticate via SSO for accessing Enterpret."
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: 'text.disabled' }}
            />
          </Tooltip>
        </Stack>
        <Switch
          disabled={isLoading}
          checked={isSSOEnforced}
          onChange={onEnforceSSOToggle}
        />
      </Stack>
    </>
  );
};
